import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";

const links = [
  { id: "who", label: "Who We Are" },
  { id: "build", label: "What We Build" },
  { id: "categories", label: "Categories" },
  { id: "serve", label: "Who We Serve" },
  { id: "why", label: "Why Blude" },
  { id: "contact", label: "Get Involved" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-2xl bg-white/75 border-b border-[#CED4DA]/60"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 flex items-center justify-between h-[72px]">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center group"
        >
          <img
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="Blude TechX"
            className="h-5 md:h-6 w-auto object-contain transition-transform duration-500 group-hover:scale-[1.03]"
          />
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-[13px] font-medium text-[#5F6368] hover:text-[#111111] tracking-wide transition-colors"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-cta"
            onClick={() => go("contact")}
            className="inline-flex items-center gap-2 rounded-full bg-[#111111] text-white px-5 py-2.5 text-[13px] font-semibold hover:bg-[#D62828] transition-colors"
          >
            Reach Out
            <span className="text-[15px] leading-none">→</span>
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-[#111111]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Open menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile overlay */}
      <div
        data-testid="mobile-menu"
        className={`lg:hidden fixed inset-0 bg-[#F8F9FA] transition-all duration-500 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        style={{ top: 72 }}
      >
        <div className="px-6 py-10 flex flex-col gap-2">
          {links.map((l, i) => (
            <button
              key={l.id}
              data-testid={`mobile-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-left font-display text-4xl tracking-tight text-[#111111] py-3 border-b border-[#CED4DA]/60"
              style={{
                transitionDelay: `${i * 60}ms`,
                transform: open ? "translateY(0)" : "translateY(20px)",
                opacity: open ? 1 : 0,
                transition: "all 600ms cubic-bezier(0.22,1,0.36,1)",
              }}
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => go("contact")}
            data-testid="mobile-cta"
            className="mt-8 inline-flex w-full justify-center items-center rounded-full bg-[#D62828] text-white py-4 font-semibold"
          >
            Reach Out
          </button>
        </div>
      </div>
    </header>
  );
}
